import api from './api';
import { Feed } from '../types/feed';
import { dashboardDataService } from './dashboard-data.service';
import { fetchCached, invalidateCachedRequests } from './request-cache';

const MAX_FEED_UPLOAD_BYTES = 4 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;

const loadImageFromFile = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Unable to process image "${file.name}"`));
    };
    image.src = objectUrl;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Image optimization failed'));
        return;
      }
      resolve(blob);
    }, type, quality);
  });

const optimizeImageFile = async (file: File): Promise<File> => {
  const compressibleTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
  if (!compressibleTypes.has(file.type)) {
    if (file.size > MAX_FEED_UPLOAD_BYTES) {
      throw new Error(`"${file.name}" exceeds the 4 MB upload limit.`);
    }
    return file;
  }

  const image = await loadImageFromFile(file);
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Image optimization is not supported in this browser.');
  }

  context.drawImage(image, 0, 0, width, height);
  const optimizedBlob = await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY);
  const optimizedFile = new File(
    [optimizedBlob],
    file.name.replace(/\.(png|webp)$/i, '.jpg'),
    { type: 'image/jpeg', lastModified: Date.now() },
  );

  if (optimizedFile.size <= MAX_FEED_UPLOAD_BYTES) {
    return optimizedFile;
  }
  if (file.size <= MAX_FEED_UPLOAD_BYTES) {
    return file;
  }
  throw new Error(`"${file.name}" exceeds the 4 MB upload limit even after optimization.`);
};

const prepareFeedFiles = async (files: File[]): Promise<File[]> =>
  Promise.all(
    files.map(async (file) => {
      if (file.type.startsWith('image/')) {
        return optimizeImageFile(file);
      }
      if (file.size > MAX_FEED_UPLOAD_BYTES) {
        throw new Error(`"${file.name}" exceeds the 4 MB upload limit.`);
      }
      return file;
    }),
  );

export interface FeedListParams {
  state?: string;
  limit?: number;
  skip?: number;
}

export const feedService = {
  listFeeds: async (params: FeedListParams = {}): Promise<Feed[]> => {
    return fetchCached<Feed[]>('/feeds', { params }, { ttlMs: dashboardDataService.getFeedCacheTtl() });
  },
  createFeed: async (payload: {
    title: string;
    message: string;
    state?: string;
    tags?: string[];
    mediaUrls?: string[];
    mediaFileIds?: string[];
  }): Promise<Feed> => {
    const response = await api.post('/feeds', payload);
    invalidateCachedRequests('/feeds');
    return response.data;
  },
  updateFeed: async (
    feedId: string,
    payload: Partial<{
      title: string;
      message: string;
      state?: string;
      tags?: string[];
      mediaUrls?: string[];
      mediaFileIds?: string[];
      isPinned?: boolean;
      source?: 'SYSTEM' | 'USER';
    }>,
  ): Promise<Feed> => {
    const response = await api.patch(`/feeds/${feedId}`, payload);
    invalidateCachedRequests('/feeds');
    return response.data;
  },
  deleteFeed: async (feedId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/feeds/${feedId}`);
    invalidateCachedRequests('/feeds');
    return response.data;
  },
  uploadMedia: async (files: File[]): Promise<{ fileIds: string[]; urls: string[] }> => {
    const preparedFiles = await prepareFeedFiles(files);
    const formData = new FormData();
    preparedFiles.forEach((file) => {
      formData.append('files', file);
    });
    const response = await api.post('/feeds/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return {
      fileIds: response.data?.fileIds || [],
      urls: response.data?.urls || [],
    };
  },
  toggleLike: async (feedId: string): Promise<Feed> => {
    const response = await api.post(`/feeds/${feedId}/like`);
    invalidateCachedRequests('/feeds');
    return response.data;
  },
  toggleDislike: async (feedId: string): Promise<Feed> => {
    const response = await api.post(`/feeds/${feedId}/dislike`);
    invalidateCachedRequests('/feeds');
    return response.data;
  },
  followUser: async (userId: string): Promise<{
    userId: string;
    isFollowing: boolean;
    followersCount: number;
    followingCount: number;
  }> => {
    const response = await api.post(`/follows/${userId}`);
    return response.data;
  },
  unfollowUser: async (userId: string): Promise<{
    userId: string;
    isFollowing: boolean;
    followersCount: number;
    followingCount: number;
  }> => {
    const response = await api.delete(`/follows/${userId}`);
    return response.data;
  },
};
